import type { IHandlerDescriptor, IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { SyntheticEvent } from '@/components/viewPort/SyntheticEvent';
import type { ImagesService } from './Images.service';

export class PointerHandlerAdjustContrast implements IPointerHandler {
  private _parent?: IPointerController;

  private _imagesService: ImagesService;

  private _pointers: Set<number> = new Set();

  private readonly _contrastChangeSpeedFactor = 3.5;

  private _cache?: { pointerId: number; pageX: number; pageY: number };

  private _handlerLock?: IHandlerDescriptor;

  public setParent(parent: IPointerController): void {
    this._parent = parent;
  }

  public constructor(imagesService: ImagesService) {
    this._imagesService = imagesService;
  }

  private _activate(pointerId: number, pageX: number, pageY: number): void {
    this._cache = { pointerId: pointerId, pageX: pageX, pageY: pageY };
  }

  private _deactivate(): void {
    if (this._cache) {
      this._parent?.releasePointerCapture(this._cache.pointerId);
      this._cache = undefined;
    }
    if (this._handlerLock) {
      this._handlerLock.remove();
      this._handlerLock = undefined;
    }
  }

  private _isActive(): boolean {
    return !!this._cache;
  }

  public onPointerDown(event: SyntheticEvent<PointerEvent>): void {
    if (event.originalEvent.button != 0) {
      return;
    }

    const { pointerId, pageX, pageY } = event.originalEvent;
    this._pointers.add(pointerId);

    if (this._pointers.size == 1) {
      // one finger, activate handler - set state
      this._activate(pointerId, pageX, pageY);
    } else if (this._isActive()) {
      // more than one finger and handler is active now, inactivate this handler - clean up state
      this._deactivate();
    }
  }

  public onPointerUp(event: SyntheticEvent<PointerEvent>): void {
    const { pointerId } = event.originalEvent;
    this._pointers.delete(pointerId);
    if (this._isActive()) {
      // handler was active - inactivate - cleanup state unconditionally
      this._deactivate();
    }
  }

  public onPointerCancel(event: SyntheticEvent<PointerEvent>): void {
    this.onPointerUp(event);
  }

  public onPointerMove(event: SyntheticEvent<PointerEvent>): void {
    if (!this._isActive()) {
      // we are inactive
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (!this._cache) {
      throw new Error('Cache expected to be valid when active');
    }

    const { pointerId, pageX, pageY } = event.originalEvent;

    if (this._cache.pointerId != pointerId) {
      // we've seen this could happen on iPad
      this._deactivate();
      return;
    }

    if (!this._handlerLock) {
      this._parent?.setPointerCapture(pointerId);
      this._handlerLock = this._parent?.pushPointerHandler(this);
    }

    const dX = pageX - this._cache.pageX;
    const dY = pageY - this._cache.pageY;
    this._performContrast(dX, dY);

    this._cache.pageX = pageX;
    this._cache.pageY = pageY;
  }

  private _performContrast(dX: number, dY: number): void {
    const contrast = this._imagesService.getContrast();
    const range = this._imagesService.getGVMinMax();

    if (!contrast || !range) {
      throw new Error('Invalid contrast values');
    }

    let minFrom = 0;
    let maxFrom = 0;

    //let movementToContrast =
    //  ((range.max - range.min + 1) / 4096) * this._contrastChangeSpeedFactor;
    const movementToContrast = this._contrastChangeSpeedFactor;
    // decide what to update
    if (Math.abs(dX) >= Math.abs(dY)) {
      // window position
      let delta = Math.abs(dX) < 3 && movementToContrast > 1 ? dX : dX * movementToContrast; // precise contrast changing case (up to 2px movement)
      delta = delta > 0 ? Math.ceil(delta) : Math.floor(delta);
      minFrom = contrast.min + delta;
      maxFrom = contrast.max + delta;
    } else {
      // window width
      let delta = Math.abs(dY) < 3 && movementToContrast > 1 ? dY : dY * movementToContrast; // precise contrast changing case (up to 2px movement)
      delta = delta > 0 ? Math.ceil(delta) : Math.floor(delta);
      minFrom = contrast.min - delta;
      maxFrom = contrast.max + delta;
    }

    // make it to fit the min-max range
    minFrom = Math.min(Math.max(minFrom, range.min), range.max);
    maxFrom = Math.min(Math.max(maxFrom, range.min), range.max);

    // ensure min is min
    minFrom = Math.min(minFrom, maxFrom);

    // ensure non zero width to avoid crash of itk
    if (minFrom === maxFrom) {
      if (minFrom > range.min) {
        minFrom = minFrom - 1;
      } else {
        maxFrom = maxFrom + 1;
      }
    }

    this._imagesService.setContrast({ min: minFrom, max: maxFrom });
  }
}
